import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RoleAssignmentService } from '../role-assignment.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { TalentService } from '../talent.service';

@Component({
    selector: 'app-role-assignment',
    templateUrl: './role-assignment.component.html',
    styleUrls: ['./role-assignment.component.css'],
    standalone: false
})
export class RoleAssignmentComponent implements OnInit {
  displayedColumns: string[] = ['id', 'role_title', 'client_name', 'recruiter_name', 'update_time', 'acct_mgr_name', 'actions'];
  dataSource: MatTableDataSource<any>;
  isLoading = true;
  errorMessage = '';
  
  activeUsers: any[] = [];
  openRoles: any[] = [];
  clients: any[] = [];
  
  newAssignment = {
    recruiter: null,
    acct_mgr: null,
    role_id: null
  };
  
  editMode = false;
  currentAssignmentId: number | null = null;
  show_detail: Object = {}; // Track which rows are expanded

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  isAdmin = false;
  userId: number;

  constructor(
    private roleAssignmentService: RoleAssignmentService,
    private dialog: MatDialog,
    private talentService: TalentService
  ) {
    this.userId = parseInt(localStorage.getItem('user_id') || '0', 10);
    this.isAdmin = this.userId === 1 || this.userId === 1020;
  }

  // Toggle the expansion of a row
  detail_toggle(id: number): void {
    console.log('detail_toggle called with id:', id);
    this.show_detail[id] = !this.show_detail[id];
    console.log('show_detail[id] is now:', this.show_detail[id]);
    
    // If expanding the row, load the full role details
    if (this.show_detail[id]) {
      this.loadRoleDetails(id);
    }
  }
  
  // Load the full role details for the expanded row
  loadRoleDetails(roleId: number): void {
    console.log('loadRoleDetails called with roleId:', roleId);
    console.log('dataSource.data:', this.dataSource.data);
    
    // Find the role in the dataSource
    const roleIndex = this.dataSource.data.findIndex(role => role.role_id === roleId);
    console.log('roleIndex:', roleIndex);
    
    if (roleIndex === -1) {
      console.log('Role not found in dataSource');
      return;
    }
    
    // If we already have the full details, no need to load again
    if (this.dataSource.data[roleIndex].fullDetailsLoaded) {
      console.log('Role details already loaded');
      return;
    }
    
    // Get the role details from the talent service
    console.log('Calling getRole with roleId:', roleId);
    this.talentService.getRole(roleId).subscribe(
      (roleDetails) => {
        console.log('Role details received:', roleDetails);
        
        // Ensure the role has all the properties expected by RoleDetailComponent
        // Make sure arrays are always initialized properly
        if (!roleDetails.work_permission || !Array.isArray(roleDetails.work_permission)) {
          roleDetails.work_permission = [];
        }
        
        if (!roleDetails.recruiters || !Array.isArray(roleDetails.recruiters)) {
          roleDetails.recruiters = [];
        }
        
        if (!roleDetails.sourcers || !Array.isArray(roleDetails.sourcers)) {
          roleDetails.sourcers = [];
        }
        
        const defaultProperties = {
          recruiters: [],
          sourcers: [],
          account_manager: null,
          status_string: '',
          status: 0,
          on_hold_notes: '',
          closed_reason: '',
          client: '',
          title: '',
          contact_id: null,
          contact_name: '',
          contact_title: '',
          type_string: '',
          category_string: '',
          skills_string: '',
          business_knowledge_string: '',
          experience: '',
          compensation: '',
          type: 0,
          bill_rate_string: '',
          location: '',
          location_state_abbr: '',
          open_date: '',
          internal_notes: '',
          client_description: '',
          description: '',
          screening_questions: '',
          screening_answers: '',
          new_york_disclosure: ''
        };
        
        // Apply default values for missing properties
        for (const [key, value] of Object.entries(defaultProperties)) {
          if (roleDetails[key] === undefined) {
            roleDetails[key] = value;
          }
        }
        
        // Merge the role details with the existing role data
        this.dataSource.data[roleIndex] = {
          ...this.dataSource.data[roleIndex],
          ...roleDetails,
          fullDetailsLoaded: true
        };
        
        // Update the dataSource to reflect the changes
        this.dataSource.data = [...this.dataSource.data];
        console.log('dataSource.data updated:', this.dataSource.data);
      },
      (error) => {
        console.error('Error loading role details:', error);
      }
    );
  }
  
  isExpanded = (row: any) => {
    console.log('isExpanded called with row:', row);
    
    // If row is a number (index), return false
    if (typeof row === 'number') {
      console.log('Row is a number, returning false');
      return false;
    }
    
    // Check if row has role_id property
    if (!row.role_id) {
      console.log('Row does not have role_id property:', row);
      return false;
    }
    
    const expanded = this.show_detail[row.role_id] === true;
    console.log('isExpanded returning:', expanded, 'for role_id:', row.role_id);
    return expanded;
  }

  ngOnInit(): void {
    this.loadRoleAssignments();
    this.loadActiveUsers();
    this.loadOpenRoles();
    this.loadClients();
    
    // Listen for pagination events
    if (this.paginator) {
      this.paginator.page.subscribe(() => {
        // Force change detection to update the view
        this.dataSource._updateChangeSubscription();
      });
    }
  }

  loadClients(): void {
    this.talentService.getYodaweClients().subscribe(
      (data) => {
        this.clients = data;
      },
      (error) => {
        console.error('Error loading clients:', error);
      }
    );
  }

  loadRoleAssignments(): void {
    this.isLoading = true;
    this.roleAssignmentService.getRoleAssignments().subscribe(
      (data) => {
        console.log('Role assignments data received:', data);
        
        // Ensure all rows have the required properties initialized
        data.forEach(row => {
          if (!row.work_permission || !Array.isArray(row.work_permission)) {
            row.work_permission = [];
          }
          if (!row.recruiters || !Array.isArray(row.recruiters)) {
            row.recruiters = [];
          }
          if (!row.sourcers || !Array.isArray(row.sourcers)) {
            row.sourcers = [];
          }
        });
        
        this.dataSource = new MatTableDataSource(data);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading role assignments:', error);
        this.errorMessage = 'Failed to load role assignments. Please try again later.';
        this.isLoading = false;
      }
    );
  }

  loadActiveUsers(): void {
    this.roleAssignmentService.getActiveUsers().subscribe(
      (data) => {
        this.activeUsers = data;
      },
      (error) => {
        console.error('Error loading active users:', error);
      }
    );
  }

  loadOpenRoles(): void {
    this.roleAssignmentService.getOpenRoles().subscribe(
      (data) => {
        this.openRoles = data;
      },
      (error) => {
        console.error('Error loading open roles:', error);
      }
    );
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    
    if (this.dataSource) {
      this.dataSource.filter = filterValue;
      
      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }

  createAssignment(): void {
    if (!this.newAssignment.recruiter || !this.newAssignment.acct_mgr || !this.newAssignment.role_id) {
      this.errorMessage = 'Please fill in all required fields.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    if (this.editMode && this.currentAssignmentId) {
      // Update existing assignment
      this.roleAssignmentService.updateRoleAssignment(this.currentAssignmentId, this.newAssignment).subscribe(
        (response) => {
          this.loadRoleAssignments();
          this.resetForm();
          this.isLoading = false;
        },
        (error) => {
          console.error('Error updating role assignment:', error);
          this.errorMessage = 'Failed to update role assignment. Please try again.';
          this.isLoading = false;
        }
      );
    } else {
      // Create new assignment
      this.roleAssignmentService.createRoleAssignment(this.newAssignment).subscribe(
        (response) => {
          this.loadRoleAssignments();
          this.resetForm();
          this.isLoading = false;
        },
        (error) => {
          console.error('Error creating role assignment:', error);
          this.errorMessage = 'Failed to create role assignment. Please try again.';
          this.isLoading = false;
        }
      );
    }
  }

  editAssignment(assignment: any): void {
    this.editMode = true;
    this.currentAssignmentId = assignment.id;
    this.newAssignment = {
      recruiter: assignment.recruiter,
      acct_mgr: assignment.acct_mgr,
      role_id: assignment.role_id
    };
  }

  deleteAssignment(id: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: { 
        title: 'Confirm Delete', 
        message: 'Are you sure you want to delete this role assignment?' 
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.roleAssignmentService.deleteRoleAssignment(id).subscribe(
          () => {
            this.loadRoleAssignments();
            this.isLoading = false;
          },
          (error) => {
            console.error('Error deleting role assignment:', error);
            this.errorMessage = 'Failed to delete role assignment. Please try again.';
            this.isLoading = false;
          }
        );
      }
    });
  }

  deleteAllAssignments(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { 
        title: 'Confirm Delete All', 
        message: 'Are you sure you want to delete ALL role assignments? This action cannot be undone.' 
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.roleAssignmentService.deleteAllRoleAssignments().subscribe(
          (response) => {
            console.log('Delete all response:', response);
            this.loadRoleAssignments();
            this.isLoading = false;
          },
          (error) => {
            console.error('Error deleting all role assignments:', error);
            this.errorMessage = 'Failed to delete all role assignments. Please try again.';
            this.isLoading = false;
          }
        );
      }
    });
  }

  resetForm(): void {
    this.newAssignment = {
      recruiter: null,
      acct_mgr: null,
      role_id: null
    };
    this.editMode = false;
    this.currentAssignmentId = null;
    this.errorMessage = '';
  }

  compareById(item1: any, item2: any): boolean {
    return item1 && item2 && item1 === item2;
  }
}
